import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import _ from "lodash";
import useConfirmDialog from "../../../utils/useConfirmDialogue";
import biosenseAPI from "../../../api/biosense";
import { useTranslation } from "react-i18next";

const NotifSettingsScreen = () => {
  const [savedNotifications, setSavedNotifications] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const { t } = useTranslation();
  const confirmAction = useConfirmDialog();
  useEffect(() => {
    biosenseAPI.getNotifSettings().then((res) => {
      setSavedNotifications(res.settings);
      setNotifications(res.settings);
    });
  }, []);

  const handleToggle = (event, type) => {
    const { checked } = event.target;
    setNotifications((prevState) => ({
      ...prevState,
      [type]: checked ? 1 : 0,
    }));
  };

  const handleSubOptionChange = (event, type) => {
    const { name, checked } = event.target;
    setNotifications((prevState) => {
      let options = prevState[type] !== null ? prevState[type].split(" ") : [];
      if (checked) {
        options = [...options, name];
      } else {
        options = options.filter((e) => e !== name);
      }
      return {
        ...prevState,
        [type]: options.length < 1 ? null : options.join(" "),
      };
    });
  };
  const handleDigestEmailChange = (event) => {
    setNotifications((prevState) => ({
      ...prevState,
      frequency: event.target.value,
    }));
  };
  const handleClearAll = () => {
    confirmAction(
      {
        title: "Clear All Settings?",
        text: "",
      },
      () => {
        setNotifications((prevState) => ({
          ...prevState,
          ...protoNotifSettings,
        }));
      },
    );
  };
  const handleDiscard = () => {
    confirmAction(
      {
        title: "Discard Changes?",
        text: "",
      },
      () => {
        if (_.isEqual(savedNotifications, notifications)) {
          return;
        }
        biosenseAPI.getNotifSettings().then((res) => setNotifications(res));
      },
    );
  };

  const handleSave = () => {
    confirmAction(
      {
        title: "Save Changes?",
        text: "",
        icon: "info",
      },
      () => {
        if (_.isEqual(savedNotifications, notifications)) {
          return;
        }
        //upload changes
      },
    );
  };

  const isEmailEnabled = notifications?.is_email;

  const options = [
    { text: t("notifsettings.sensoractivity"), option: "s_activity" },
    { text: t("notifsettings.sensoralert"), option: "s_alert" },
    { text: t("notifsettings.iotactivity"), option: "iot_activity" },
    { text: t("notifsettings.iotalert"), option: "iot_alert" },
  ];

  if (notifications === null) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
        <CircularProgress sx={{ mt: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%" }}>
      <Card sx={{ minHeight: "100%", boxShadow: 4 }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant={"h5"} sx={{ my: 2, mx: 2 }}>
            {t("notifsettings.notifsettings")}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {[
              { type: "email", text: t("notifsettings.email") },
              { type: "push", text: t("notifsettings.push") },
              { type: "sms", text: t("notifsettings.sms") },
              { type: "badge", text: t("notifsettings.badge") },
            ].map(({ type, text }) => (
              <React.Fragment key={type}>
                <ListItem>
                  <Grid container alignItems='center'>
                    <Grid item sm={4} lg={2}>
                      <ListItemText primary={<span>{text}</span>} />
                    </Grid>
                    <Grid item xs={8} lg={2} gap={1}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { sm: "flex-end", lg: "flex-start" },
                          alignItems: "center",
                          px: 2,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications[`is_${type}`]}
                              onChange={(event) => handleToggle(event, `is_${type}`)}
                            />
                          }
                        />
                        <Typography variant={"body"}>
                          {notifications[`is_${type}`]
                            ? t("notifsettings.on")
                            : t("notifsettings.off")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={8} gap={1}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { sm: "space-between", lg: "flex-start" },
                          alignItems: "center",
                          px: 2,
                        }}
                      >
                        {options.map(({ text, option }, index) => (
                          <FormControlLabel
                            key={`${option}${index}`}
                            control={
                              <Checkbox
                                checked={
                                  notifications[type] !== null
                                    ? notifications[type].split(" ").includes(option)
                                    : false
                                }
                                onChange={(event) => handleSubOptionChange(event, type)}
                                name={option}
                                disabled={!notifications[`is_${type}`]}
                              />
                            }
                            label={text}
                          />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          <Typography
            variant={"h5"}
            sx={{ mt: 4, mb: 2, mx: 2, color: !isEmailEnabled ? "text.disabled" : "text.primary" }}
          >
            {t("notifsettings.digestemail")}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            <ListItem>
              <Grid container alignItems='center'>
                <Grid item sm={12} lg={2}>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          color: !isEmailEnabled ? "text.disabled" : "text.primary",
                        }}
                      >
                        {t("notifsettings.frequency")}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item sm={12} lg={10}>
                  <RadioGroup
                    row
                    aria-labelledby='frequency-label'
                    name='frequency'
                    value={notifications.frequency}
                    onChange={handleDigestEmailChange}
                    sx={{
                      display: "flex",
                      justifyContent: { sm: "space-between", lg: "flex-start" },
                      alignItems: "center",
                      px: 2,
                    }}
                  >
                    <FormControlLabel
                      value='off'
                      control={<Radio disabled={!isEmailEnabled} />}
                      label={t("notifsettings.off")}
                    />
                    <FormControlLabel
                      value='daily'
                      control={<Radio disabled={!isEmailEnabled} />}
                      label={t("notifsettings.daily")}
                    />
                    <FormControlLabel
                      value='weekly'
                      control={<Radio disabled={!isEmailEnabled} />}
                      label={t("notifsettings.weekly")}
                    />
                    <FormControlLabel
                      value='monthly'
                      control={<Radio disabled={!isEmailEnabled} />}
                      label={t("notifsettings.monthly")}
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
            </ListItem>
          </List>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Stack direction='row' spacing={1}>
              <Button variant='outlined' color='warning' onClick={handleClearAll}>
                {t("notifsettings.clearall")}
              </Button>
              <Button variant='outlined' color='error' onClick={handleDiscard}>
                {t("notifsettings.discardchanges")}
              </Button>
              <Button variant='contained' color='primary' onClick={handleSave}>
                {t("notifsettings.save")}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NotifSettingsScreen;

const protoNotifSettings = {
  is_email: 0,
  email: null,
  is_push: 0,
  push: null,
  is_sms: 0,
  sms: null,
  is_badge: 0,
  badge: null,
  frequency: "weekly",
};
